const VERSION_DETECTION_PATTERN = /^v?\d+(\.\d+){2,}(-[0-9A-Za-z.-]+)?$/;
const VERSION_PATTERN = /^v?(\d+(?:\.\d+)*)(?:-([0-9A-Za-z.-]+))?$/;

interface ParsedVersion {
  numericParts: number[];
  prerelease: string | null;
}

function parseVersion(label: string): ParsedVersion | null {
  const match = VERSION_PATTERN.exec(label);
  if (!match) {
    return null;
  }
  return {numericParts: match[1].split(`.`).map(Number), prerelease: match[2] ?? null};
}

/**
 * A column is sorted as versions only when most of its labels look like x.y.z versions.
 */
export function shouldUseVersionSort(labels: string[]): boolean {
  if (labels.length === 0) {
    return false;
  }
  const versionLabelCount = labels.filter((label) => VERSION_DETECTION_PATTERN.test(label)).length;
  return versionLabelCount > labels.length / 2;
}

/**
 * Compares labels in the direction of sortOrder (1 ascending, -1 descending). Labels that do not parse as versions,
 * such as "(not set)", always sort after versions regardless of direction. Missing numeric parts count as 0 and a
 * prerelease sorts just below its base version.
 */
export function compareVersionLabels(a: string, b: string, sortOrder: number): number {
  const aVersion = parseVersion(a);
  const bVersion = parseVersion(b);
  if (!aVersion || !bVersion) {
    if (aVersion) {
      return -1;
    }
    if (bVersion) {
      return 1;
    }
    return sortOrder * a.localeCompare(b);
  }
  return sortOrder * compareParsedVersions(aVersion, bVersion, a, b);
}

function compareParsedVersions(aVersion: ParsedVersion, bVersion: ParsedVersion, a: string, b: string): number {
  const partCount = Math.max(aVersion.numericParts.length, bVersion.numericParts.length);
  for (let i = 0; i < partCount; i++) {
    const difference = (aVersion.numericParts[i] ?? 0) - (bVersion.numericParts[i] ?? 0);
    if (difference !== 0) {
      return difference;
    }
  }

  if (aVersion.prerelease !== bVersion.prerelease) {
    if (aVersion.prerelease === null) {
      return 1;
    }
    if (bVersion.prerelease === null) {
      return -1;
    }
    return aVersion.prerelease < bVersion.prerelease ? -1 : 1;
  }
  return a.localeCompare(b);
}
